<template>
	<div v-show="isAnnotationSelected" class="stickyTools">
		<div class="deleteText" @click="deleteAnnotation">
			<span class="icon" />
		</div>
		<div v-show="canChangeFont" class="fontSizeUp" @click="increaseFont">
			<span class="icon" />
		</div>
		<div v-show="canEditText" class="edit" @click="editText">
			<span class="icon" />
		</div>
		<div v-show="canChangeFont" class="fontSizeDown" @click="decreaseFont">
			<span class="icon" />
		</div>
		<div v-show="canResize" class="annotationIncrease" @click="enlargeAnnotation">
			<span class="icon" />
		</div>
		<div v-show="canResize" class="annotationDecrease" @click="shrinkAnnotation">
			<span class="icon" />
		</div>
	</div>
</template>


<script>
import { mapActions, mapState } from "vuex";
import { textMeasure } from "./Annotations.js";
import AnnotationType from "../../models/annotationType.js";
import { isLineShapes } from '@syncfusion/ej2-pdfviewer';
import * as Annotations from "./Annotations.js";

const isTextAnnotation = (annotation) => annotation?.type === AnnotationType.FreeText || annotation?.shapeAnnotationType === AnnotationType.FreeText;

const isInkAnnotation = (annotation) => annotation?.shapeAnnotationType === AnnotationType.Ink;
const isBlackLineAnnotation = (annotation) => annotation?.shapeAnnotationType === AnnotationType.BlackLine;
const isStampAnnotation = (annotation) => annotation?.shapeAnnotationType === AnnotationType.Stamp;

export default {
	inject: {
		pdfViewer: { type: Object },
		getSelectedAnnotationId: { type: Function },
		getAnnotationById: { type: Function },
		selectAnnotation: { type: Function },
	},
	data() {
		return {
			selectedAnnotation: null,
		};
	},
	computed: {
		isAnnotationSelected() {
			return !!this.selectedAnnotation;
		},
		canEditText() {
			return this.canChangeFont && !this.selectedAnnotation.isReadonly;
		},
		canChangeFont() {
			const annotation = this.selectedAnnotation;
			return isTextAnnotation(annotation);
		},
		canResize() {
			const annotation = this.selectedAnnotation;
			const isSupportedAnnotation = isInkAnnotation(annotation) || isBlackLineAnnotation(annotation) || isStampAnnotation(annotation);
			return isSupportedAnnotation;
		},
		...mapState([`author`]),
	},
	watch: {
		'pdfViewer.value'(pdfViewer) {
			// pdf viewer properties are not reactive, so we use event listeners to get currently selected annotation
			pdfViewer.addEventListener(`annotationSelect`, (event) => {
				const annotation = this.getAnnotationById(event.annotationId);
				if (annotation)
					this.selectedAnnotation = annotation;
			});
			// the select event is not fired when an image annotation is added
			pdfViewer.addEventListener(`annotationAdd`, () => this.selectedAnnotation = { annotation: {} });
			pdfViewer.addEventListener(`annotationUnSelect`, () => this.selectedAnnotation = null);
			// the unselect event is not fired when an annotation is removed
			pdfViewer.addEventListener(`annotationRemove`, () => this.selectedAnnotation = null);
			// the unselect event is not fired when the document is changed
			pdfViewer.addEventListener(`documentLoad`, () => this.selectedAnnotation = null);
		}
	},
	methods: {
		increaseFont() {
			this.changeFont(2);
		},
		editText() {
			const annotationDetails = this.tryGetAnnotation();
			if (!annotationDetails) return;
			const { viewer, annotationBase, page } = annotationDetails;
			const freeText = viewer.annotation.freeTextAnnotationModule;
			viewer.annotation.annotationSelect(annotationBase.id, page.index, annotationBase, null, true, true);
			const { x, y } = annotationBase.bounds;			
			freeText.addInuptElemet({ x, y }, annotationBase);
			if (freeText.inputBoxElement.value === Annotations.customTextContent) freeText.inputBoxElement.select();
		},
		decreaseFont() {
			this.changeFont(-2);
		},
		enlargeAnnotation() {
			this.resizeAnnotation(0.2);
		},
		shrinkAnnotation() {
			this.resizeAnnotation(-0.2);
		},
		tryGetAnnotation() {
			const annotationId = this.getSelectedAnnotationId();
			// If no annotation is selected, abort
			if (!annotationId) return false;
			const viewer = this.pdfViewer.value;
			const annotation = this.getAnnotationById(annotationId);
			// If annotation wasn't found, abort
			if (!annotation) return false;
			const annotationBase = viewer.selectedItems.annotations[0];
			const index = annotation.pageIndex ?? annotation.pageNumber;
			// If the page is not rendered, abort
			if (!document.getElementById(`pdfviewer_pageDiv_${index}`)) {
				return false;
			}
			const width = viewer.viewerBase.pageSize[index].width;
			const height = viewer.viewerBase.pageSize[index].height;
			const margin = 10;
			return { viewer, annotation, annotationBase, page: { index, width, height, margin } };
		},
		parseBounds(bounds) {
			let { left, width, top, height, x, y } = bounds;
			// some annotations are using x, y instead of left, top
			left = left ?? x;
			top = top ?? y;
			// some annotations have undefined height
			height ??= 0;
			return { left, width, top, height };
		},
		resizeBounds(bounds, delta, page) {
			let { left, width, top, height } = bounds;
			const widthDelta = delta * width;
			width += widthDelta;
			left -= widthDelta / 2;
			// if left-right bounds are in the margin, move them
			if (left + width + page.margin > page.width) left = page.width - page.margin - width;
			if (left < page.margin) left = page.margin;
			// if the right bound is in the margin after moving, abort
			if (left + width + page.margin > page.width) return false;

			const heightDelta = delta * height;
			height += heightDelta;
			top -= heightDelta / 2;
			// if top-bottom bounds are in the margin, move them
			if (top + height + page.margin > page.height) top = page.height - page.margin - height;
			if (top < page.margin) top = page.margin;
			// if the top bound is in the margin after moving, abort
			if (top + height + page.margin > page.height) return false;

			return { left, width, top, height, x: left, y: top };
		},
		createChangeNode(bounds, annotation, annotationBase) {
			const { left, width, top, height } = bounds;
			let node = { bounds: { width, height, x: left + width / 2, y: top + height / 2 } };
			if (isLineShapes(annotationBase)) {
				const vertexPoints = annotationBase.vertexPoints;
				// If we don't have exactly 2 vertex points, this is not a black line annotation. Should never happen.
				if (!vertexPoints.length === 2) return false;
				vertexPoints[0].x = left;
				vertexPoints[1].x = left + width;
				// need to update vertexPoints in the annotation object
				annotation.vertexPoints = vertexPoints;
				const leaderHeight = annotationBase.leaderHeight;
				node = { ...node, vertexPoints, leaderHeight };
			}
			// need to update bounds in the annotation object 
			annotation.bounds = bounds;
			return node;
		},
		resizeAnnotation(delta) {
			const annotationDetails = this.tryGetAnnotation();
			if (!annotationDetails) return;
			const { viewer, annotation, annotationBase, page } = annotationDetails;
			const bounds = this.resizeBounds(this.parseBounds(annotation.bounds), delta, page);
			if (!bounds) return;
			const changeNode = this.createChangeNode(bounds, annotation, annotationBase);
			if (!changeNode) return;
			viewer.nodePropertyChange(annotationBase, changeNode);
			viewer.renderSelector(page.index, this.pdfViewer.annotationSelectorSettings);
			viewer.annotationModule.modifyInCollections(annotationBase, `bounds`);
			viewer.annotationModule.triggerAnnotationPropChange(annotationBase, false, false, false, true);
			viewer.renderDrawing();
		},
		changeFont(delta) {
			const annotationDetails = this.tryGetAnnotation();
			if (!annotationDetails) return;
			const { viewer, annotation } = annotationDetails;
			viewer.annotationModule.deleteAnnotation();
			this.syncAnnotationRemove(annotation.annotationId);
			annotation.fontSize += delta;
			const measure = textMeasure(
				annotation.dynamicText,
				annotation.fontSize,
				annotation.fontFamily
			);
			annotation.bounds.width = measure.width;
			annotation.bounds.height = measure.height;
			viewer.addAnnotation(annotation);
			this.syncAnnotationAdd(annotation);
			this.selectAnnotation(annotation);
		},
		deleteAnnotation() {
			const viewer = this.pdfViewer.value;
			const annotation = viewer.selectedItems?.annotations[0];
			if (annotation) {
				// deletes the selected annotation
				viewer.annotationModule.deleteAnnotation();
				this.syncAnnotationRemove(annotation.annotName);
			}
		},
		...mapActions(`AnnotationsSync`, [
			`syncAnnotationAdd`,
			`syncAnnotationRemove`,
		]),
	}
};
</script>